/* eslint-disable no-param-reassign */
import { sum } from 'lodash';
import { ChantContext } from '../ChantContext';

export enum TextSpanType {
  Normal = 0,  // default
  Bold = 1,  // <b></b>
  Italic = 2,  // <i></i>
  Special = 4,  // <sp></sp>
  Verbatim = 8,  // <v></v>
  Colored = 16, // <c></c>
  Euouae = 32, // <eu></eu>
  NonBreaking = 64, // <nlba></nlba>
  AboveLinesText = 128, // <alt></alt>
}

export const SPECIAL_CHARACTERS: { [key: string]: string | number } = {
  'ae': 'æ',
  'oe': 'œ',
  '\'ae': 'ǽ',
  '\'oe': 'œ́',
  '\'æ': 'ǽ',
  '\'œ': 'œ́',
  'A/': 0xE03F,
  'R/': 0xE040,
  'V/': 0xE041,
  '*': 0xE028,
  '+': 0xE029,
};

export class TextSpan {
  private _text: string;
  private _type: TextSpanType;

  get text() {
    if (this._type === TextSpanType.Verbatim) {
      return '';
    }

    return this._text;
  }

  get type() {
    return this._type;
  }

  constructor(
    text: string,
    type: TextSpanType = TextSpanType.Normal,
  ) {
    this._text = text.replace(/<[^>]+>/g, '');
    this._type = type;
  }

  getSvgSpan(context: ChantContext, isAboveLinesText: boolean, {
    adjustment = 0,
    vAdjustment = 0,
    rAdjustment = 0,
    aAdjustment = 0,
  }): [(SVGTSpanElement | null)[], number] {
    const text = this.text;
    if (!text) {
      return [[null], 0];
    }

    if (text.includes('~')) {
      const parts = text.split('~');
      const resultSpans: (SVGTSpanElement | null)[] = [];
      const adjustments = [];
      for (const part of parts) {
        const [resultSpan, newAdjustment] = this.getInternalSvgSpan(context, new TextSpan(part, this._type),
          isAboveLinesText, {
          adjustment,
          vAdjustment,
          rAdjustment,
          aAdjustment,
        });
        if (resultSpan) {
          resultSpans.push(resultSpan);
        }
        resultSpans.push(this.getEmptySVGSpan());
        adjustments.push(newAdjustment);
      }
      resultSpans.pop();
      return [resultSpans, sum(adjustments) / adjustments.length];
    } else {
      const [resultSpan, resultAdjustment] = this.getInternalSvgSpan(context, this, isAboveLinesText, {
        adjustment,
        vAdjustment,
        rAdjustment,
        aAdjustment,
      });
      return [[resultSpan], resultAdjustment];
    }
  }

  getEmptySVGSpan(): SVGTSpanElement {
    const el = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
    el.textContent = '~';
    el.style.opacity = '0';
    return el;
  }

  getInternalSvgSpan(context: ChantContext, span: TextSpan, aboveLinesText: boolean, {
    adjustment = 0,
    vAdjustment = 0,
    rAdjustment = 0,
    aAdjustment = 0,
  }): [SVGTSpanElement | null, number] {

    const spanEl = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');

    spanEl.textContent = span.text.replace(/[{}]/g, '');

    if (!spanEl.textContent) {
      return [null, 0];
    }

    if (adjustment) {
      spanEl.setAttribute('dx', String(adjustment));
      adjustment = 0;
    }

    if (this.type & TextSpanType.Colored) {
      spanEl.setAttribute('fill', 'red');
    }

    if (this.type & TextSpanType.Bold) {
      spanEl.setAttribute('font-weight', 'bold');
    }

    if (this.type & TextSpanType.Italic) {
      spanEl.setAttribute('font-style', 'italic');
    }

    if (this.type & TextSpanType.Special) {
      const realChar = SPECIAL_CHARACTERS[spanEl.textContent];
      spanEl.setAttribute('font-family', context.specialCharactersFont);

      if (typeof realChar === 'string') {
        spanEl.textContent = realChar || spanEl.textContent;
      } else {
        if (spanEl.textContent.endsWith('/')) {
          const barEl = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
          barEl.textContent = String.fromCharCode(realChar);

          const letterEl = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
          letterEl.textContent = spanEl.textContent.slice(0, -1);
          if (letterEl.textContent === 'V') {
            adjustment = vAdjustment;
          } else if (letterEl.textContent === 'A') {
            adjustment = aAdjustment;
          } else if (letterEl.textContent === 'R') {
            adjustment = rAdjustment;
          }
          barEl.setAttribute('dx', String(-adjustment));

          spanEl.textContent = '';
          spanEl.appendChild(letterEl);
          spanEl.appendChild(barEl);
        } else {
          spanEl.textContent = String.fromCharCode(realChar);
        }
      }
    }

    if (this.type & TextSpanType.AboveLinesText && !aboveLinesText) {
      spanEl.textContent = "";
      // ignore
    }

    if (this.type & TextSpanType.Verbatim) {
      // ignore
    }

    return [spanEl, adjustment];
  }
}
